import React, { useState, useCallback } from 'react';
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonSpinner,
  IonIcon,
  IonTextarea,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import { save, add, trash, create } from 'ionicons/icons';
import { useTags } from './tags-provider';
import { TagType } from './tags-schema';

export type TagEditorMode = 'products' | 'tags';
export type TagEditorLayout = 'table' | 'cards';

interface UnifiedTagEditorProps {
  mode: TagEditorMode;
  layout?: TagEditorLayout;
  title?: string;
  emptyMessage?: string;
  addButtonText?: string;
  nameLabel?: string;
  descriptionLabel?: string;
}

const UnifiedTagEditor: React.FC<UnifiedTagEditorProps> = ({
  mode,
  layout = 'cards',
  title,
  emptyMessage,
  addButtonText,
  nameLabel,
  descriptionLabel
}) => {
  const { tags, addTag, updateTag, deleteTag, loadingTags } = useTags();
  const [editStates, setEditStates] = useState<Record<string, { editing: boolean, name: string, description: string }>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter tags based on mode
  const filteredTags = tags?.filter(tag => 
    mode === 'products' ? tag.isProduct : !tag.isProduct
  ) || [];

  // Default labels based on mode
  const defaultTitle = title || (mode === 'products' ? 'Products' : 'Tags');
  const defaultEmptyMessage = emptyMessage || (mode === 'products' 
    ? 'No products have been created yet. Click "Add New Product" to create your first product.'
    : 'No tags have been created yet. Click "Add New Tag" to create your first tag.'
  );
  const defaultAddButtonText = addButtonText || (mode === 'products' ? 'Add New Product' : 'Add New Tag');
  const defaultNameLabel = nameLabel || (mode === 'products' ? 'Product Name' : 'Tag Name');
  const defaultDescriptionLabel = descriptionLabel || 'Description';

  const startEditing = useCallback((tag: TagType) => {
    setEditStates(prev => ({
      ...prev,
      [tag.id]: {
        editing: true,
        name: tag.displayName,
        description: tag.description || ''
      }
    }));
  }, []);

  const startAddingNew = useCallback(() => {
    setIsAddingNew(true);
    setEditStates(prev => ({
      ...prev,
      'new': {
        editing: true,
        name: '',
        description: ''
      }
    }));
  }, []);

  const cancelEditing = useCallback((tagId: string) => {
    setEditStates(prev => {
      const newState = { ...prev };
      delete newState[tagId];
      return newState;
    });
    if (tagId === 'new') {
      setIsAddingNew(false);
    }
  }, []);

  const updateEditState = useCallback((tagId: string, field: 'name' | 'description', value: string) => {
    setEditStates(prev => ({
      ...prev,
      [tagId]: {
        ...prev[tagId],
        [field]: value
      }
    }));
  }, []);

  const handleSave = useCallback(async (tag: TagType | null) => {
    const editState = editStates[tag?.id || 'new'];
    if (!editState) return;

    if (!editState.name.trim()) {
      setError(`${defaultNameLabel} cannot be empty`);
      return;
    }

    try {
      if (tag) {
        // Update existing tag
        await updateTag(tag.id, {
          ...tag,
          displayName: editState.name,
          description: editState.description || undefined
        });
      } else {
        // Create new tag
        await addTag({
          displayName: editState.name,
          description: editState.description || undefined,
          isProduct: mode === 'products'
        });
      }
      cancelEditing(tag?.id || 'new');
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [editStates, updateTag, addTag, cancelEditing, mode, defaultNameLabel]);

  const handleDeleteTag = useCallback(async (tagId: string) => {
    try {
      await deleteTag(tagId);
      cancelEditing(tagId);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [deleteTag, cancelEditing]);

  if (loadingTags) {
    return <IonSpinner />;
  }

  const renderEditForm = (tag: TagType | null, isNew: boolean = false) => {
    const editState = editStates[tag?.id || 'new'];
    const tagId = tag?.id || 'new';

    return (
      <div>
        <IonItem>
          <IonLabel position="stacked">{defaultNameLabel}</IonLabel>
          <IonInput
            value={editState?.name || ''}
            onIonInput={e => updateEditState(tagId, 'name', e.detail.value!)}
            placeholder={`Enter ${defaultNameLabel.toLowerCase()}`}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{defaultDescriptionLabel}</IonLabel>
          <IonTextarea
            value={editState?.description || ''}
            onIonInput={e => updateEditState(tagId, 'description', e.detail.value!)}
            rows={2}
            autoGrow
            placeholder={`Enter ${defaultDescriptionLabel.toLowerCase()} (optional)`}
          />
        </IonItem>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <IonButton
            size="small"
            onClick={() => handleSave(tag)}
            disabled={!editState?.name?.trim()}
          >
            <IonIcon slot="start" icon={save} />
            {isNew ? (mode === 'products' ? 'Create Product' : 'Create Tag') : 'Save'}
          </IonButton>
          <IonButton
            size="small"
            fill="outline"
            onClick={() => cancelEditing(tagId)}
          >
            Cancel
          </IonButton>
        </div>
      </div>
    );
  };

  const renderCardLayout = () => (
    <div>
      {error && (
        <IonItem color="danger">
          <IonLabel>{error}</IonLabel>
        </IonItem>
      )}

      <div style={{ marginTop: '2rem' }}>
        <IonText>
          <h3>{defaultTitle} ({filteredTags.length})</h3>
        </IonText>
        
        <div style={{ marginTop: '1rem' }}>
          {/* Existing items */}
          {filteredTags.map(tag => {
            const editState = editStates[tag.id];
            const isEditing = editState?.editing || false;

            return (
              <IonItem key={tag.id} style={{ marginBottom: '0.5rem' }}>
                <div style={{ width: '100%' }}>
                  {isEditing ? (
                    renderEditForm(tag)
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <IonText>
                          <h4 style={{ margin: '0 0 0.25rem 0' }}>{tag.displayName}</h4>
                        </IonText>
                        {tag.description && (
                          <IonText color="medium">
                            <p style={{ margin: '0', fontSize: '0.9rem' }}>{tag.description}</p>
                          </IonText>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <IonButton
                          size="small"
                          fill="clear"
                          onClick={() => startEditing(tag)}
                        >
                          <IonIcon icon={create} />
                        </IonButton>
                        <IonButton
                          size="small"
                          fill="clear"
                          color="danger"
                          onClick={() => handleDeleteTag(tag.id)}
                        >
                          <IonIcon icon={trash} />
                        </IonButton>
                      </div>
                    </div>
                  )}
                </div>
              </IonItem>
            );
          })}

          {/* New item creation interface */}
          {isAddingNew && (
            <IonItem style={{ marginBottom: '0.5rem', border: '2px dashed #ccc' }}>
              <div style={{ width: '100%' }}>
                {renderEditForm(null, true)}
              </div>
            </IonItem>
          )}

          {/* Add new item button */}
          {!isAddingNew && (
            <IonItem style={{ marginTop: '1rem' }}>
              <IonButton
                onClick={startAddingNew}
                expand="block"
                fill="outline"
              >
                <IonIcon slot="start" icon={add} />
                {defaultAddButtonText}
              </IonButton>
            </IonItem>
          )}

          {/* Empty state */}
          {filteredTags.length === 0 && !isAddingNew && (
            <IonText color="medium">
              <p>{defaultEmptyMessage}</p>
            </IonText>
          )}
        </div>
      </div>
    </div>
  );

  const renderTableLayout = () => (
    <div className="tag-editor">
      {error && <IonText color="danger" style={{ marginBottom: '1rem' }}>{error}</IonText>}

      {/* Existing items */}
      {filteredTags.length === 0 ? (
        <p>{defaultEmptyMessage}</p>
      ) : (
        <IonGrid>
          <IonRow className="header-row">
            <IonCol size="4">{defaultNameLabel}</IonCol>
            <IonCol size="5">{defaultDescriptionLabel}</IonCol>
            <IonCol size="3">Actions</IonCol>
          </IonRow>
          
          {filteredTags.map((tag) => {
            const isEditing = editStates[tag.id]?.editing;
            
            return (
              <IonRow key={tag.id} className="item-row">
                <IonCol size="4">
                  {isEditing ? (
                    <IonInput
                      value={editStates[tag.id].name}
                      onIonChange={(e) => updateEditState(tag.id, 'name', e.detail.value || '')}
                    />
                  ) : (
                    tag.displayName
                  )}
                </IonCol>
                <IonCol size="5">
                  {isEditing ? (
                    <IonTextarea
                      value={editStates[tag.id].description}
                      onIonChange={(e) => updateEditState(tag.id, 'description', e.detail.value || '')}
                    />
                  ) : (
                    tag.description || <span className="text-muted">No description</span>
                  )}
                </IonCol>
                <IonCol size="3" className="action-buttons">
                  {isEditing ? (
                    <>
                      <IonButton size="small" onClick={() => handleSave(tag)}>
                        <IonIcon slot="icon-only" icon={save} />
                      </IonButton>
                      <IonButton size="small" fill="outline" onClick={() => cancelEditing(tag.id)}>
                        Cancel
                      </IonButton>
                    </>
                  ) : (
                    <>
                      <IonButton size="small" onClick={() => startEditing(tag)}>
                        <IonIcon slot="icon-only" icon={create} />
                      </IonButton>
                      <IonButton size="small" color="danger" onClick={() => handleDeleteTag(tag.id)}>
                        <IonIcon slot="icon-only" icon={trash} />
                      </IonButton>
                    </>
                  )}
                </IonCol>
              </IonRow>
            );
          })}
        </IonGrid>
      )}

      {/* Add New Item */}
      <div className="add-item-section">
        <h4>{defaultAddButtonText}</h4>
        <IonGrid>
          <IonRow>
            <IonCol size="12" sizeMd="6">
              <IonItem>
                <IonLabel position="stacked">{defaultNameLabel}</IonLabel>
                <IonInput
                  value={editStates['new']?.name || ''}
                  onIonChange={(e) => updateEditState('new', 'name', e.detail.value || '')}
                  placeholder={`Enter ${defaultNameLabel.toLowerCase()}`}
                />
              </IonItem>
            </IonCol>
            <IonCol size="12" sizeMd="6">
              <IonItem>
                <IonLabel position="stacked">{defaultDescriptionLabel}</IonLabel>
                <IonTextarea
                  value={editStates['new']?.description || ''}
                  onIonChange={(e) => updateEditState('new', 'description', e.detail.value || '')}
                  placeholder={`Enter ${defaultDescriptionLabel.toLowerCase()} (optional)`}
                />
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonButton 
                expand="block" 
                onClick={() => handleSave(null)}
                disabled={!editStates['new']?.name?.trim()}
              >
                <IonIcon slot="start" icon={add} />
                {mode === 'products' ? 'Add Product' : 'Add Tag'}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>

      <style>{`
        .tag-editor {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .header-row {
          font-weight: bold;
          border-bottom: 1px solid #ccc;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .item-row {
          border-bottom: 1px solid #eee;
          padding: 8px 0;
          align-items: center;
        }
        .text-muted {
          color: #999;
          font-style: italic;
        }
        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .add-item-section {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid #ccc;
        }
      `}</style>
    </div>
  );

  return layout === 'table' ? renderTableLayout() : renderCardLayout();
};

export default UnifiedTagEditor;
